import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  CalendarClockIcons,
  ChartIcons,
  FileQuestionIcons,
  LightningIcons,
  ListCheckIcons,
  MagicIcons,
} from '../../../../icons/icons';
import { Button } from '../../../../ui/components/Common';
import { ClockIcon } from '../../../../ui/icons/icons';
import { unixToCurrentTimezone } from '../../../../utils/helpers/time';
import { ParticipantCardData } from '../index';

type ParticipantCardProps = {
  userAssessment: ParticipantCardData;
};

type StatusMapping = {
  [status: string]: {
    action: string;
    buttonLabel: string;
    classes: string;
    icon: React.ReactNode;
  };
};

const isPast = (endDate: number): boolean => {
  const currentUnixTimestamp = Math.floor(Date.now() / 1000);
  return endDate < currentUnixTimestamp;
};

const statusMappings: StatusMapping = {
  assigned: {
    action: 'Get started with your assessment',
    buttonLabel: 'Start Now',
    classes: 'bg-emerald-500 text-white shadow-lg shadow-emerald-200',
    icon: <MagicIcons className='w-4 h-4' />,
  },
  'in progress': {
    action: 'Continue where you left off',
    buttonLabel: 'Continue',
    classes: 'bg-amber-500 text-white shadow-lg shadow-amber-200',
    icon: <LightningIcons className='w-4 h-4' />,
  },
  expired: {
    action: 'This assessment has expired',
    buttonLabel: 'View Results',
    classes: 'bg-slate-600 text-white',
    icon: null,
  },
  completed: {
    action: 'You have completed this assessment',
    buttonLabel: 'View Results',
    classes: 'bg-blue-500 text-white shadow-lg shadow-blue-200',
    icon: null,
  },
};

const ParticipantCard = ({ userAssessment }: ParticipantCardProps) => {
  const history = useHistory();
  const apiStatus = (userAssessment.status || '').toLowerCase();
  const assessmentExpired = isPast(userAssessment.endDate);
  // Only apply date-based expiry if the assessment wasn't explicitly completed/expired by the server.
  // If the server has already marked it 'completed' or 'expired', trust that over end-date math.
  const normalizedStatus =
    apiStatus === 'completed' || apiStatus === 'expired'
      ? apiStatus
      : assessmentExpired
        ? 'expired'
        : apiStatus;
  const mapping = statusMappings[normalizedStatus] || {
    action: 'No action required',
    buttonLabel: 'Details',
    classes: 'bg-gray-100 text-gray-600',
    icon: null,
  };

  const buttonClick = () => {
    history.push('/assigned-assessments');
  };

  const startDateStr = unixToCurrentTimezone(userAssessment.startDate).split(
    ',',
  )[0];
  const endDateStr = unixToCurrentTimezone(userAssessment.endDate).split(
    ',',
  )[0];

  return (
    <div className='group relative flex flex-col bg-white rounded-[20px] overflow-hidden border border-slate-200 border-1 shadow-[0_22px_60px_rgba(0,0,0,0.07)] hover:-translate-y-2.5 transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)]'>
      <div className='flex flex-col h-full gap-4 p-6'>
        { /* Header: Icon & Badge */ }
        <div className='flex items-start justify-between '>
          <div className='relative'>
            <div className='absolute inset-0 bg-emerald-100 blur-2xl opacity-10 rounded-full'></div>
            <div className='relative w-15 h-15 bg-[#f8fafc] rounded-[22px] flex items-center justify-center border border-slate-50 shadow-sm group-hover:scale-110 transition-transform duration-500'>
              { normalizedStatus === 'completed' ? (
                <div className='p-2 bg-emerald-50 rounded-xl text-emerald-500'>
                  <ListCheckIcons className='w-7 h-7' />
                </div>
              ) : (
                <div className='p-2 bg-blue-50/50 rounded-xl text-slate-700'>
                  <MagicIcons className='w-7 h-7' />
                </div>
              ) }
            </div>
          </div>

          <div
              className={`px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.18em] shadow-sm border border-white/10 ${
                normalizedStatus === 'completed'
                  ? 'bg-[#10b981] text-white'
                  : normalizedStatus === 'expired'
                    ? 'bg-slate-400 text-white'
                    : 'bg-[#0f172a] text-white'
              }`}
          >
            { normalizedStatus === 'assigned' ? 'Active' : normalizedStatus }
          </div>
        </div>

        { /* Title & Subtitle Section */ }
        <div className='flex flex-col '>
          <h3 className='text-[23px] font-extrabold text-slate-900 leading-[1.15] mb-2 tracking-tight group-hover:text-emerald-700 transition-colors duration-300'>
            { userAssessment.assessmentTitle }
          </h3>
        </div>

        { /* Vertical Data List - Standardized to reference structure */ }
        <div className='flex-1 flex flex-col gap-6 '>
          { /* Status-specific Data Rows */ }
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3.5'>
              <CalendarClockIcons className='w-4.5 h-4.5 text-slate-300/80' />
              <span className='text-[13.5px] font-bold text-slate-400'>
                { normalizedStatus === 'completed' ? 'Finished' : 'Assigned' }
              </span>
            </div>
            <span className='text-[13.5px] font-black text-slate-800 uppercase tracking-tighter'>
              { startDateStr }
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3.5'>
              { normalizedStatus === 'completed' ? (
                <ListCheckIcons className='w-4.5 h-4.5 text-slate-300/80' />
              ) : (
                <ClockIcon className='w-4.5 h-4.5 text-slate-300/80' />
              ) }
              <span className='text-[13.5px] font-bold text-slate-400'>
                { normalizedStatus === 'completed' ? 'Completed' : 'Deadline' }
              </span>
            </div>
            <span
                className={`text-[13.5px] font-black uppercase tracking-tighter ${
                  normalizedStatus === 'completed'
                    ? 'text-[#10b981]'
                    : assessmentExpired
                      ? 'text-rose-500'
                      : 'text-[#0ea5e9]'
                }`}
            >
              { normalizedStatus === 'completed'
                ? ''
                : assessmentExpired
                  ? 'Expired'
                  : '' }
            </span>
          </div>
        </div>

        { /* Action Button Section */ }
        <div className='mt-auto pt-2'>
          { normalizedStatus !== 'expired' &&
          normalizedStatus !== 'completed' ? (
            <Button
                  variant='primary'
                  label={
                  mapping.buttonLabel === 'Start Now'
                    ? 'START ASSESSMENT'
                    : 'CONTINUE SESSION'
                }
                  onClick={buttonClick}
                  disabled={!userAssessment.isActive}
                  className='w-full !px-4 !py-2  !text-[12px] !font-black !rounded-[20px] !bg-gradient-to-r !from-[#0ea5e9] !to-[#10b981] !border-none shadow-xl shadow-emerald-200/40 hover:shadow-emerald-300/60 hover:scale-[1.01] active:scale-[0.98] transition-all duration-500 uppercase tracking-[0.2em]'
              />
            ) : (
              <></>
            ) }
        </div>
      </div>
    </div>
  );
};

export default ParticipantCard;
